import { createRef, PureComponent } from 'react';

import { hasCommandModifier } from 'my-utils';

import EquationField from 'my-elements/EquationField';
import Latex from 'my-elements/Latex';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

let cachedLatexMode = false;

export default class EquationEditor extends PureComponent {
  state = { latex: this.props.value || '', latexMode: cachedLatexMode };
  latexInput = createRef();

  componentDidMount() {
    this._focusTimeout = setTimeout(this.focus); // wait for popper to reposition before focusing
    window.addEventListener('keydown', this.handleKeyDown);
  }
  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    clearTimeout(this._focusTimeout);
  }
  handleKeyDown = e => {
    if (hasCommandModifier(e) && e.key === 'Enter') {
      this.confirmEquation(e);
      e.preventDefault();
      // Process the event here (such as click on submit button)
    }
    if (e.key === 'Escape') {
      this.props.onClose(e);
    }
    if (hasCommandModifier(e) && e.key === 'l' && e.shiftKey) {
      this.toggleLatexMode();
    }
  };

  confirmEquation = e => {
    const { onClose, onUpdate } = this.props;
    const { latex } = this.state;
    onUpdate(latex);
    onClose(e);
  };

  toggleLatexMode = () => {
    const { latexMode } = this.state;
    cachedLatexMode = !latexMode;
    this.setState({ latexMode: !latexMode });
  };

  updateLatex = latex => this.setState({ latex });

  focus = () => {
    const { latexMode } = this.state;
    if (latexMode) {
      this.latexInput.current?.focus();
    }
  };

  render() {
    const { anchorEl, displayMode, onClose } = this.props;
    const { latex, latexMode } = this.state;
    return (
      <Popper anchorEl={anchorEl} css={rootCss} open={true} placement="bottom">
        <ClickAwayListener mouseEvent="onMouseDown" onClickAway={onClose}>
          <Paper className="EE-paper" elevation={3}>
            <Typography align="center" variant="h6">
              Equation Editor
            </Typography>
            <Tooltip disableInteractive title="⇧⌘L">
              <FormControlLabel
                control={<Checkbox checked={latexMode} onChange={this.toggleLatexMode} />}
                label="Latex"
              />
            </Tooltip>
            {latexMode ?
              <div className="EE-wrapper">
                <TextField
                  autoFocus
                  fullWidth
                  inputRef={this.latexInput}
                  multiline
                  onChange={e => this.updateLatex(e.target.value)}
                  placeholder="Enter latex here"
                  value={latex}
                />
                <Latex className="EE-latex" displayMode={displayMode}>
                  {latex}
                </Latex>
              </div>
            : <div className="EE-wrapper">
                <EquationField autoFocus onChange={this.updateLatex} onEnter={this.confirmEquation} value={latex} />
              </div>
            }
            <Tooltip disableInteractive title="⌘⏎">
              <Button fullWidth onClick={this.confirmEquation} variant="contained">
                Done
              </Button>
            </Tooltip>
          </Paper>
        </ClickAwayListener>
      </Popper>
    );
  }
}

const rootCss = ({ spacing, zIndex }) => ({
  zIndex: zIndex.modal + 2,
  '.EE-paper': {
    textAlign: 'center',
    minWidth: 300,
    padding: spacing(2),
  },
  '.EE-wrapper': { padding: spacing(1, 0) },
  '.EE-latex': {
    marginTop: spacing(1),
    textAlign: 'center',
  },
});
